import { getResourceStore } from "./crudFactory";

/**
 * 負責關係要在畫面上長成一張看得懂的卡片（ticket 33，Figma 76:13187）。
 *
 * 卡片要顯示的名稱與那兩到四個辨識欄位,都在被指向的那筆資產上。
 * 由 mock 這邊一起算出來回給前端 —— 不然轉派彈窗要為了八個類別各發一次查詢,
 * 而真正的後端本來就會把這些欄位跟關係一起回。
 *
 * 注意:加一個資源類別要改兩個地方 —— constants/workResources.js(類別本身)
 * 與這裡的 SHAPES(卡片顯示什麼)。少改這裡的話卡片會退成只有名稱。
 */
const rowOf = (resource, recordId) => {
  const store = getResourceStore(resource);
  return store ? store.find(recordId) : null;
};

const roomName = (roomId) => {
  const room = rowOf("room", roomId);
  return room ? room.name : "";
};

const pair = (label, value, extra) => ({
  label,
  value: value === undefined || value === null || value === "" ? "—" : String(value),
  ...extra,
});

/**
 * 金額欄:交給卡片用 `<Money>` 依 RBAC 遮罩,不在這裡先格式化成字串。
 *
 * 缺值要走回 `pair()` 的「—」—— `<Money value={undefined}>` 會渲染成一個
 * 孤零零的「NT$」(Money 對非有限數直接回傳原值,undefined 什麼都不印)。
 * 那看起來像「金額是空的」,實際上是「這筆沒有金額欄位」。
 */
const amountPair = (label, value) =>
  (value === undefined || value === null || value === ""
    ? pair(label, value)
    : { label, value, mask: "amount" });

// 每一類要顯示哪些欄位:依設計稿的卡片,取最能辨識這筆資料的兩到四項
const SHAPES = {
  room: (r) => ({
    label: r.name,
    detail: [pair("地址", r.address), pair("樓層／區域", [r.floor, r.zone].filter(Boolean).join("／"))],
  }),
  cabinet: (r) => ({
    label: r.code,
    detail: [
      pair("機房", roomName(r.roomId)),
      pair("廠牌型號", [r.brand, r.model].filter(Boolean).join("-")),
      pair("總U", r.uTotal),
      pair("容量", r.capacityKva ? `${r.capacityKva} KVA` : ""),
    ],
  }),
  // 設計稿的硬體卡片是「廠牌型號／序號／版本」,但硬體資料上沒有版本欄位
  // (韌體/BIOS 版本尚未納入資產結構)。這裡先用 IP 補第三格並記在 ticket 33,
  // 等硬體補上版本欄位再換回來 —— 不要放一個永遠是「—」的欄位。
  hardware: (r) => ({
    label: [r.code, r.name].filter(Boolean).join(" "),
    detail: [
      pair("廠牌型號", [r.brand, r.model].filter(Boolean).join(" ")),
      pair("序號", r.serialNo),
      pair("IP", r.ip),
    ],
  }),
  software: (r) => ({
    label: [r.code, r.name].filter(Boolean).join("　"),
    detail: [
      pair("廠牌型號", [r.brand, r.model].filter(Boolean).join("・")),
      pair("序號", r.serialNo),
      // 稿的第三格是「CVE ⟨等級⟩（含KEV）」—— 有 KEV 才加註,沒有就只寫等級
      pair("CVE", [r.cveLevel, r.kevLevel ? "（含KEV）" : ""].filter(Boolean).join("")),
    ],
  }),
  // ⚠️ 設計稿外(缺資料):稿的 IP 卡片是「遮罩／閘道／關聯硬體」,
  // 但 IP 資料上只有用途與設備代號,沒有 mask/gateway/關聯硬體 ——
  // 放三個永遠是「—」的欄位比放現有欄位更沒用。缺口記在票 33。
  ip: (r) => ({
    label: r.ip,
    detail: [pair("用途", r.purpose), pair("設備", r.deviceCode)],
  }),
  project: (r) => ({
    label: r.name,
    // 稿只有「期間」一格(2128:23550 的專案卡 detail 只有一列一格)
    detail: [pair("期間", [r.startDate, r.endDate].filter(Boolean).join(" – "))],
  }),
  contract: (r) => ({
    label: r.name,
    // 稿是「服務廠商／服務等級／合約金額」;服務等級不在合約資料上(缺口記在票 33),
    // 其餘照稿 —— 金額標 mask,由卡片交給 <Money> 依角色遮罩。
    detail: [pair("服務廠商", r.vendorName), amountPair("合約金額", r.amount)],
  }),
  vendor: (r) => ({
    label: r.name,
    detail: [pair("類別", r.type), pair("聯絡人", r.contact)],
  }),
  // 訊息規則管理(WF_v3 票 21):稿的卡片樣式未畫到欄位層級,
  // 取規則最能辨識的兩項 —— 分類與發送管道
  "notify-rule": (r) => ({
    label: r.name,
    // category 的值本身就是中文分類字串(constants/notifyCategories.js)
    detail: [pair("分類", r.category), pair("管道", r.channel)],
  }),
};

/** 補上卡片要用的 label / detail;找不到目標資料就標示出來,不要靜靜給一張空卡 */
export const decorateWorkItem = (relation) => {
  const row = rowOf(relation.resource, relation.recordId);
  if (!row) {
    return { ...relation, label: `（已刪除 #${relation.recordId}）`, detail: [] };
  }
  const shape = SHAPES[relation.resource];
  return { ...relation, ...(shape ? shape(row) : { label: String(row.name || row.code || relation.recordId), detail: [] }) };
};
